import { deleteRequest, getRequest, postRequest, putRequest } from '@helpers/api';
import { keysToSnake } from '@helpers/camelCaseConverter';
import type { Column } from '@models/ConversionTool/conversionToolColumnsGet/types';

import type {
  Conversion,
  CreateConversionPayload,
  MappingPayloadData,
  Mapping,
  MappingPreview,
  MappingSheet,
  Maps,
  SheetNames,
} from './types';

/**
 * Gets vat number
 */
export const getMappingList = (
  /** vat number unique identifier */
  entityJurisdictionId: string
): Promise<Maps> =>
  getRequest('conversion-tool/mapping/', { entity_jurisdiction: entityJurisdictionId });

/**
 * Gets sheets
 */
export const getSheets = (
  /** entity unique identifier */
  entity: string
): Promise<SheetNames> => getRequest(`conversion-tool/sheets/${entity}/`);

/**
 * Creates a mapping
 */
export const createMapping = (payload: MappingPayloadData) =>
  postRequest('conversion-tool/mapping/', keysToSnake(payload));

/**
 * Creates conversion
 */
export const createConversion = (payload: CreateConversionPayload): Promise<Conversion> =>
  postRequest('conversion-tool/conversion/', keysToSnake(payload));

/**
 * Gets a mapping sheet
 */
export const getMappingSheet = ({
  fileId,
  sheetName,
  rowStart,
  previewSize,
  headerStart,
}: MappingSheet): Promise<MappingPreview> =>
  getRequest(`conversion-tool/preview/${fileId}/`, {
    sheet_name: sheetName,
    row_start: rowStart,
    preview_size: previewSize,
    header_start: headerStart,
  });

/**
 * Deletes a mapping
 * @param mappingId
 */
export const deleteMapping = (
  /** mapping unique identifier */
  mappingId: string
): Promise<MappingPreview> => deleteRequest(`conversion-tool/mapping/${mappingId}/`);

/**
 * Gets a mapping
 */
export const getMapping = (
  /** mapping unique identifier */
  mappingId: string
): Promise<Mapping> => getRequest(`conversion-tool/mapping/${mappingId}/`);

/**
 *  Updates the selected mapping
 */
export const updateMapping = ({
  data = {} as Mapping,
  mappingId = '',
}: {
  /** mapping data */
  data?: Mapping;
  /** mapping unique identifier */
  mappingId?: string;
}): Promise<Mapping> => putRequest(`conversion-tool/mapping/${mappingId}/`, keysToSnake(data));

/**
 * Gets conversion tool columns
 */
export const getConversionToolColumns = ({
  registrationId,
  filingTypeId,
}: {
  /** Registration id */
  registrationId?: string;
  /** Filing type  id */
  filingTypeId?: string;
}): Promise<{ columns: Array<Column> }> =>
  getRequest('conversion-tool/columns/', keysToSnake({ registrationId, filingTypeId }));
